import type { Doc } from "building-types/doc";
import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { Group } from "building-types/groups/group";
import type { GroupableEntityType } from "building-types/handles/docEntityHandleTypes";
import { groupableEntityTypes, type GroupableEntity, isGroupableEntityBySubType } from "./docEntityGroupable";
import { docEntityTypeToDocTableName } from "./docEntityTypeToDocTableName";
import { docGetCollapsedTableEntries } from "./docGetCollapsedTableEntries";
import { docGetGroupedAndUngroupedEntitiesBySubType } from "./docGetGroupBySubType";
import type { DocGroupedAndUngroupedEntityTable } from "./groupedAndUngroupedEntityList";

export function docGetGroupedAndUngroupedEntities<T extends GroupableEntityType>(
  doc: Doc,
  entityType?: T,
  predicate: (entity: DocEntityTypeToValueType[T]) => boolean = () => true,
): DocGroupedAndUngroupedEntityTable {
  const groupedAndUngroupedEntities = {};

  const entityTypes: GroupableEntityType[] = entityType
    ? [entityType]
    : (Object.keys(groupableEntityTypes).filter((key) => groupableEntityTypes[key]) as GroupableEntityType[]);

  for (const currentEntityType of entityTypes) {
    const docTableName = docEntityTypeToDocTableName[currentEntityType];

    if (!docTableName) {
      continue;
    }

    // Intentionally omit includeSystemEntities: Doc Ref / settings catalogs list
    // doc-owned entities only (unlike Explorer, which merges system tables).
    // includeEntityGroups stays false — only the collapsed top-level list is needed.
    const { entries } = docGetCollapsedTableEntries(doc, currentEntityType, {
      includeGroups: true,
      entityPredicate: (entity) => predicate(entity as DocEntityTypeToValueType[T]),
    });

    const sortedEntitiesAndGroups: (GroupableEntity | Group)[] = entries.map((entry) => entry.value);

    if (isGroupableEntityBySubType(currentEntityType)) {
      groupedAndUngroupedEntities[docTableName] = docGetGroupedAndUngroupedEntitiesBySubType(
        doc,
        docTableName,
        sortedEntitiesAndGroups,
      );
    } else {
      groupedAndUngroupedEntities[docTableName] = sortedEntitiesAndGroups;
    }
  }

  return groupedAndUngroupedEntities;
}
